import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import planApi from '../../services/planApi';
import config from '../../config';
import {
  Target, Calendar, TrendingUp, CheckCircle, Clock,
  ArrowRight, ShieldCheck, Compass, ListChecks, CalendarDays, Lock
} from 'lucide-react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useToast } from '../../hooks/useToast';
import MainLayout from '../layout/MainLayout';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import KPICard from '../core/KPICard/KPICard';
import NotificationsWidget from '../common/NotificationsWidget';
import '../../styles/mentee-dashboard.scss';

const getImageUrl = (imagePath?: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const baseUrl = config.api.baseUrl.replace(/\/api\/?$/, '');
  return `${baseUrl}/${imagePath}`;
};

const fmtDateTime = (sql?: string) => {
  if (!sql) return '—';
  const d = new Date(String(sql).replace(' ', 'T'));
  return isNaN(d.getTime()) ? String(sql) : d.toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' });
};

type StageState = 'done' | 'active' | 'locked';

const MenteeDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState<any>(null);
  const [plan, setPlan] = useState<any>(null);
  const [comps, setComps] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    try {
      setLoading(true);
      const dResp = await apiService.mentee.dashboard();
      const d = dResp.data.data;
      setDash(d);
      const relId = d?.active_relationship?.id;
      if (relId) {
        const [p, pdi, s] = await Promise.all([
          planApi.getPlan(relId).catch(() => null),
          planApi.getPdi(relId).catch(() => ({ competencies: [] })),
          planApi.getSessions(relId).catch(() => ({ sessions: [] })),
        ]);
        setPlan(p);
        setComps((pdi as any)?.competencies || []);
        setSessions((s as any)?.sessions || []);
      }
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="mentee-dashboard mentee-dashboard--loading">
          <LoadingSpinner size="lg" text="Cargando tu programa..." />
        </div>
      </MainLayout>
    );
  }

  const mentor = dash?.current_mentor;
  const hasMentor = !!mentor;

  // ---- Estado del plan ----
  const consentOk = plan?.locks?.fodaEnabled === true;
  const fodaDone = plan?.stages?.foda?.status === 'finalized';
  const pdiDone = plan?.stages?.pdi?.status === 'finalized';

  // ---- Avance del PDI (real, basado en acciones marcadas) ----
  const totalActions = comps.reduce((n, c) => n + (Number(c.actionsTotal) || 0), 0);
  const doneActions = comps.reduce((n, c) => n + (Number(c.actionsDone) || 0), 0);
  const pdiProgress = totalActions > 0 ? Math.round((doneActions / totalActions) * 100) : 0;

  // ---- Sesiones ----
  const now = new Date();
  const upcoming = sessions
    .filter(s => ['scheduled', 'pending'].includes(s.status))
    .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)));
  const nextSession = upcoming.find(s => new Date(String(s.scheduled_date).replace(' ', 'T')) >= now) || upcoming[0];
  const completedSessions = sessions.filter(s => s.status === 'completed').length;

  // ---- Objetivos (competencias) cerrados y meses restantes ----
  const totalObjectives = comps.length;
  const closedObjectives = comps.filter((c: any) => Number(c.actionsTotal) > 0 && Number(c.actionsDone) === Number(c.actionsTotal)).length;
  const monthsRemaining = plan?.program?.months_remaining ?? 11;

  const objectivesOf = (s: any): string[] =>
    (s.objectives && s.objectives.length) ? s.objectives : (s.objective ? [s.objective] : []);

  const stages: { key: string; label: string; state: StageState; icon: React.ReactNode; hint: string }[] = [
    { key: 'foda', label: 'FODA', state: fodaDone ? 'done' : (consentOk ? 'active' : 'locked'), icon: <Compass size={18} />, hint: fodaDone ? 'Completado' : (consentOk ? 'En curso' : 'Bloqueado') },
    { key: 'pdi', label: 'Plan de Desarrollo Individual', state: pdiDone ? 'done' : (fodaDone ? 'active' : 'locked'), icon: <ListChecks size={18} />, hint: fodaDone ? `${pdiProgress}% de avance` : 'Bloqueado' },
    { key: 'agenda', label: 'Sesiones', state: consentOk ? 'active' : 'locked', icon: <CalendarDays size={18} />, hint: consentOk ? `${completedSessions} sesión(es) hechas` : 'Bloqueado' },
  ];

  return (
    <MainLayout>
      <div className="mentee-dashboard">
        {/* Header */}
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">Mentee</div>
            <h1 className="heading-1">Mi Programa de <em>Mentoría</em></h1>
            {hasMentor ? (
              <div className="plan-counterpart">
                <span className="plan-counterpart-avatar">
                  {getImageUrl(mentor.profile_image)
                    ? <img src={getImageUrl(mentor.profile_image) as string} alt={`${mentor.first_name} ${mentor.last_name}`} />
                    : <span aria-hidden>{(mentor.first_name?.[0] || '')}{(mentor.last_name?.[0] || '')}</span>}
                </span>
                <span className="plan-counterpart-info">
                  <span className="plan-counterpart-role">Tu mentor</span>
                  <span className="plan-counterpart-name">{mentor.first_name} {mentor.last_name}</span>
                </span>
              </div>
            ) : (
              <div className="page-meta">Sin mentor asignado aún</div>
            )}
          </div>
          <div className="page-header__actions">
            <button className="btn btn--primary" onClick={() => navigate('/mentee/plan')}>
              Ir a mi plan <ArrowRight size={16} />
            </button>
          </div>
        </div>

        {/* Aviso: consentimiento pendiente (solo si no está aceptado) */}
        {!consentOk && (
          <div className="consent-alert">
            <div className="consent-alert__box" role="alert">
              <span className="consent-alert__icon"><ShieldCheck size={20} /></span>
              <div className="consent-alert__text">
                <strong>Consentimiento pendiente.</strong> Acepta el acuerdo de confidencialidad para comenzar tu plan de mentoría.
              </div>
              <button className="consent-alert__cta" onClick={() => navigate('/mentee/agreement')}>
                Revisar y aceptar <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}

        {/* Estado del plan */}
        <div className="plan-status" role="navigation" aria-label="Estado del plan">
          {stages.map((s, i) => (
            <button
              key={s.key}
              className={`plan-status__step plan-status__step--${s.state}`}
              onClick={() => navigate(s.key === 'agenda' ? '/mentee/sessions' : `/mentee/plan?step=${s.key}`)}
              title={`Ir a ${s.label}`}
            >
              <span className="plan-status__icon">
                {s.state === 'done' ? <CheckCircle size={18} /> : s.state === 'locked' ? <Lock size={16} /> : s.icon}
              </span>
              <span className="plan-status__text">
                <span className="plan-status__label">{s.label}</span>
                <span className="plan-status__hint">{s.hint}</span>
              </span>
              {i < stages.length - 1 && <span className="plan-status__sep" aria-hidden />}
            </button>
          ))}
        </div>

        {/* Indicadores */}
        <div className="mkpis">
          <div className="mkpi">
            <div className="mkpi__num mkpi__num--red">{pdiProgress}<span className="mkpi__unit">%</span></div>
            <div className="mkpi__lbl">Avance del PDI</div>
          </div>
          <div className="mkpi">
            <div className="mkpi__num">{completedSessions}<span className="mkpi__den">/12</span></div>
            <div className="mkpi__lbl">Sesiones</div>
          </div>
          <div className="mkpi">
            <div className="mkpi__num">{closedObjectives}<span className="mkpi__den">/{totalObjectives}</span></div>
            <div className="mkpi__lbl">Objetivos cerrados</div>
          </div>
          <div className="mkpi">
            <div className="mkpi__num">{monthsRemaining}</div>
            <div className="mkpi__lbl">Meses restantes</div>
          </div>
        </div>

        {/* Grid principal */}
        <div className="dashboard-grid">
          {/* Progreso del PDI (competencias) */}
          <div className="dashboard-grid__main">
            <Card animate animateDelay={0.2}>
              <CardHeader>
                <CardTitle>Progreso del PDI</CardTitle>
              </CardHeader>
              <CardBody>
                {comps.length === 0 ? (
                  <div className="dash-empty">
                    <p>Aún no has definido tu Plan de Desarrollo Individual.</p>
                    <button className="btn btn--outline" onClick={() => navigate('/mentee/plan?step=pdi')}>
                      Construir mi PDI <ArrowRight size={15} />
                    </button>
                  </div>
                ) : (
                  <div className="comp-progress-list">
                    {comps.map((c) => {
                      const pct = Number(c.actionsTotal) > 0
                        ? Math.round((Number(c.actionsDone) / Number(c.actionsTotal)) * 100)
                        : Math.round(Number(c.progress_percentage) || 0);
                      return (
                        <div className="comp-progress" key={c.id}>
                          <div className="comp-progress__head">
                            <span className="comp-progress__title">{c.title}</span>
                            <span className="comp-progress__pct">{pct}%</span>
                          </div>
                          <div className="comp-progress__bar">
                            <div className="comp-progress__fill" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="comp-progress__meta">
                            {Number(c.actionsDone) || 0}/{Number(c.actionsTotal) || 0} acciones · origen FODA
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>

            <NotificationsWidget limit={5} animateDelay={0.28} />
          </div>

          {/* Lateral: mentor + próximas sesiones */}
          <div className="dashboard-grid__sidebar">
            <Card animate animateDelay={0.25}>
              <CardHeader><CardTitle>Mi Mentor</CardTitle></CardHeader>
              <CardBody>
                {hasMentor ? (
                  <div className="mentor-card">
                    <div className="mentor-header">
                      <div className="mentor-avatar">
                        {getImageUrl(mentor.profile_image) ? (
                          <img src={getImageUrl(mentor.profile_image) as string}
                            alt={`${mentor.first_name} ${mentor.last_name}`}
                            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                        ) : (
                          <span>{mentor.first_name?.[0]}{mentor.last_name?.[0]}</span>
                        )}
                      </div>
                      <div className="mentor-info">
                        <h4>{mentor.first_name} {mentor.last_name}</h4>
                        <p>{mentor.title || 'Mentor'}</p>
                        {mentor.company && <p className="mentor-company">{mentor.company}</p>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="dash-empty">Un administrador te asignará un mentor.</p>
                )}
              </CardBody>
            </Card>

            <Card animate animateDelay={0.3}>
              <CardHeader><CardTitle>Próximas sesiones</CardTitle></CardHeader>
              <CardBody>
                {upcoming.length === 0 ? (
                  <div className="dash-empty">
                    <p>No tienes sesiones agendadas.</p>
                    <button className="btn btn--outline" onClick={() => navigate('/mentee/sessions')}>
                      Agendar una sesión <ArrowRight size={15} />
                    </button>
                  </div>
                ) : (
                  <div className="next-sessions">
                    {upcoming.slice(0, 4).map((s) => {
                      const objs = objectivesOf(s);
                      return (
                        <div className="next-session" key={s.id}>
                          <div className="next-session__date">
                            <Clock size={14} /> {fmtDateTime(s.scheduled_date)}
                          </div>
                          {objs.length > 0 && (
                            <div className="next-session__obj">{objs.length > 1 ? 'Objetivos' : 'Objetivo'}: {objs.join(' · ')}</div>
                          )}
                          <span className={`next-session__status st-${s.status}`}>
                            {s.status === 'pending' ? 'Pendiente de aprobación' : 'Agendada'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>

      </div>
    </MainLayout>
  );
};

export default MenteeDashboard;
